import { useState, useEffect, useRef, useCallback } from "react";
import { useDraftApi } from "../services/useDraftApi";
import { useNavigate, useLocation } from "react-router";

const EXPIRY_CHECK_BUFFER_MS = 3000;

export const useConsultationDraft = (bookingId, serviceName, patientSex, serviceType) => {
  const { getDraft, getDraftStatus, saveDraftSlice, clearDraft } = useDraftApi();
  const [draft, setDraft] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const saveTimeoutRef = useRef(null);
  const expiryTimeoutRef = useRef(null);
  const skipNextSave = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectToStart = useCallback(
    (reason) => {
      if(location.pathname.includes("result-outcome")){
         navigate("/consultation-success", {
          state: {
            serviceType,
            isSuccess: true,
            rejectionMessage: `Consultation completed successfully for booking ID: ${bookingId}`,
          },
          replace: true,
        });
        return;
      }
      else{
      navigate("/patient-record", {
        replace: true,
        state: { bookingId, serviceName, patientSex, serviceType, reason },
      });}
    },
    [navigate, bookingId, serviceName, patientSex, serviceType],
  );

  const scheduleExpiryCheck = useCallback(
    (ttlRemainingSeconds) => {
      if (expiryTimeoutRef.current) clearTimeout(expiryTimeoutRef.current);
      if (ttlRemainingSeconds == null) return;

      expiryTimeoutRef.current = setTimeout(async () => {
        try {
          const status = await getDraftStatus(bookingId, serviceName);
          if (status.state === "expired") {
            redirectToStart("draft_expired");
          } else if (status.state === "active") {
            scheduleExpiryCheck(status.ttl_remaining);
          }
          else {
            redirectToStart("draft_expired");
          }
        } catch (error) {
          expiryTimeoutRef.current = setTimeout(
            () => scheduleExpiryCheck(5),
            5000,
          );
        }
      }, ttlRemainingSeconds * 1000 + EXPIRY_CHECK_BUFFER_MS);
    },
    [bookingId, serviceName, redirectToStart],
  );

  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== "visible" || !bookingId) return;
      try {
        const status = await getDraftStatus(bookingId, serviceName);
        if (status.state === "expired") {
          redirectToStart("draft_expired");
        } else if (status.state === "active") {
          scheduleExpiryCheck(status.ttl_remaining);
        }
      } catch {
        // Ignore draft status errors to avoid interrupting the current flow.
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [bookingId, serviceName, redirectToStart, scheduleExpiryCheck]);

  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const status = await getDraftStatus(bookingId, serviceName);
        if (cancelled) return;

        if (status.state === "expired") {

          redirectToStart("draft_expired");
          return;
        }

        if (status.state === "not_started") {

          skipNextSave.current = true;
          setIsLoaded(true);
          return;
        }

        const existing = await getDraft(bookingId, serviceName);
        if (cancelled) return;
        skipNextSave.current = true;
        if (existing) setDraft(existing);
        scheduleExpiryCheck(status.ttl_remaining);
      } catch (error) {
        if (!cancelled) setSaveError(error);
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (expiryTimeoutRef.current) clearTimeout(expiryTimeoutRef.current);
    };
  }, [bookingId, serviceName, redirectToStart, scheduleExpiryCheck]);

  const saveSlice = useCallback(
    (partialData) => {
      if (!bookingId) return;
      setDraft((prev) => ({ ...(prev || {}), ...partialData }));

      if (skipNextSave.current) {
        skipNextSave.current = false;
        return Promise.resolve();
      }

      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const merged = await saveDraftSlice(
            bookingId,
            serviceName,
            partialData,
          );
          setDraft(merged);
          setSaveError(null);

          scheduleExpiryCheck(merged.ttl_remaining);
        } catch (error) {
          setSaveError(error);
        }
      }, 800);
    },
    [bookingId, serviceName, scheduleExpiryCheck],
  );

  const finalizeDraft = useCallback(async () => {
    if (!bookingId) return;
    if (expiryTimeoutRef.current) clearTimeout(expiryTimeoutRef.current);
    await clearDraft(bookingId, serviceName);
  }, [bookingId, serviceName]);

  return { draft, isLoaded, saveSlice, finalizeDraft, saveError };
};